// routerHandler.ts
import {
  NavigationFailure,
  RouteLocationNamedRaw,
  RouteLocationNormalized,
  RouteLocationRaw,
  RouteQueryAndHash,
  Router,
} from 'vue-router'

import type { CollectionPagination } from '@/api/ApiClient'
import router from '@/router/index'
import store from '@/store'

type Dictionary<T> = Record<string, T>
type QueryValue = string | null | (string | null)[]

export class RouterHandler {
  private static readonly PREFIX_SEPARATOR = '~'
  public readonly DEFAULT_PAGE_SIZE = 10

  /**
   * Creates a new object with all keys prefixed
   */
  static prefixObject<T extends string | number>(
    prefix: string,
    obj: Dictionary<T>
  ): Dictionary<T> {
    return Object.entries(obj).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [`${prefix}${key}`]: value,
      }),
      {}
    )
  }

  /**
   * Extracts and processes query parameters for a given prefix
   */
  public query(prefix: string): Dictionary<string> {
    const currentQuery = router.currentRoute.value.query

    return Object.entries(currentQuery).reduce((acc, [key, value]) => {
      if (this.shouldIncludeInQuery(key, prefix, acc)) {
        const queryKey = this.stripPrefix(key, prefix)
        const stringValue = this.normalizeQueryValue(value)
        if (stringValue !== null) {
          return { ...acc, [queryKey]: stringValue }
        }
      }
      return acc
    }, {})
  }

  /**
   * Get pagination parameters from the current route
   */
  public pagination(prefix: string): CollectionPagination {
    const currentQuery = router.currentRoute.value.query
    let pageSize: number | undefined
    let page: number | undefined

    Object.entries(currentQuery).forEach(([key, value]) => {
      const strippedKey = this.stripPrefix(key, prefix)

      if (!this.hasOtherPrefix(key, prefix)) {
        if (this.isPageSizeKey(strippedKey) && !pageSize) {
          pageSize = this.parseQueryValue(value)
        } else if (strippedKey === 'page' && !page) {
          page = this.parseQueryValue(value)
        }
      }
    })

    return {
      page: page || 1,
      pageSize: pageSize || this.DEFAULT_PAGE_SIZE,
    }
  }

  /**
   * Push a new route
   */
  public async push(
    location: RouteLocationRaw
  ): Promise<NavigationFailure | void | undefined> {
    if (store.getters['global/organisationIsSwitching']) {
      // Prevent navigation if the organisation is switching
      return
    }
    try {
      return router.push(location)
    } catch (error) {
      this.handleNavigationError(error)
    }
  }

  /**
   * Resolve a route location
   */
  public resolve(
    to: RouteLocationRaw,
    current?: RouteLocationNormalized
  ): ReturnType<Router['resolve']> | undefined {
    return router.resolve(to, current)
  }

  /**
   * Replace current route
   */
  public async replace(
    location: RouteLocationRaw
  ): Promise<NavigationFailure | void | undefined> {
    if (store.getters['global/organisationIsSwitching']) {
      // Prevent navigation if the organisation is switching
      return
    }
    try {
      return router.replace(location)
    } catch (error) {
      this.handleNavigationError(error)
    }
  }

  /**
   * Remove a query parameter with the given prefix
   */
  public async removeFromQuery(
    prefix: string,
    key: string
  ): Promise<void | NavigationFailure> {
    const currentQuery = this.cloneQuery(router.currentRoute.value.query)
    delete currentQuery[`${prefix}${key}`]

    return this.replace({
      path: router.currentRoute.value.path,
      query: currentQuery,
    })
  }

  /**
   * Set query parameters with the given prefix
   */
  public async setQuery(
    prefix: string,
    query: Dictionary<unknown>
  ): Promise<NavigationFailure | void> {
    const prefixedQuery = RouterHandler.prefixObject(
      prefix,
      query as Dictionary<string | number>
    )
    const currentQuery = this.cloneQuery(router.currentRoute.value.query)

    const newQuery = this.sanitizeQuery({
      ...this.removePrefixedProps(prefix, currentQuery),
      ...prefixedQuery,
    })

    return this.replace({
      path: router.currentRoute.value.fullPath,
      query: newQuery,
    })
  }

  /**
   * Set the page number in the query
   */
  public async setPage(
    prefix: string,
    page: number
  ): Promise<NavigationFailure | void> {
    const pagination = this.pagination(prefix)
    const newPagination = RouterHandler.prefixObject(prefix, {
      page: Math.max(1, page),
      pageSize: pagination.pageSize,
    })

    return this.replace({
      path: router.currentRoute.value.path,
      query: {
        ...this.cloneQuery(router.currentRoute.value.query),
        ...newPagination,
      },
    })
  }

  /**
   * Generate a detail link with proper parameters
   */
  public getDetailLinkTo(
    name: string,
    id: string,
    copy: boolean = false,
    extraParams?: Record<string, unknown>,
    extraQuery?: RouteQueryAndHash['query']
  ): RouteLocationNamedRaw {
    const params = {
      id: copy ? '0' : id,
      ...extraParams,
    }

    return {
      name,
      params,
      query: extraQuery,
    }
  }

  // Private helper methods
  private stripPrefix(key: string, prefix: string): string {
    return key.startsWith(prefix) ? key.substring(prefix.length) : key
  }

  private hasOtherPrefix(key: string, currentPrefix: string): boolean {
    return (
      key.includes(RouterHandler.PREFIX_SEPARATOR) &&
      !key.startsWith(currentPrefix)
    )
  }

  private isPageSizeKey(key: string): boolean {
    return key === 'pageSize' || key === 'page_size'
  }

  private parseQueryValue(value: QueryValue): number | undefined {
    const stringValue = this.normalizeQueryValue(value)
    return stringValue ? parseInt(stringValue, 10) : undefined
  }

  private normalizeQueryValue(value: QueryValue): string | null {
    if (Array.isArray(value)) {
      return value[0]
    }
    return value
  }

  private shouldIncludeInQuery(
    key: string,
    prefix: string,
    acc: Dictionary<string>
  ): boolean {
    const isPaginationParam = ['pageSize', 'page'].includes(
      this.stripPrefix(key, prefix)
    )
    const hasOtherPrefix = this.hasOtherPrefix(key, prefix)
    const isAlreadyDefined = acc[key] !== undefined

    return (
      !isPaginationParam &&
      (key.startsWith(prefix) || (!hasOtherPrefix && !isAlreadyDefined))
    )
  }

  private handleNavigationError(error: unknown): void {
    console.error('Navigation failed:', error)
    // You might want to add additional error handling here
  }

  private cloneQuery(
    query: RouteLocationNormalized['query']
  ): Record<string, string> {
    return JSON.parse(JSON.stringify(query))
  }

  private sanitizeQuery(query: Dictionary<unknown>): Dictionary<string> {
    return Object.entries(query).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        return { ...acc, [key]: String(value) }
      }
      return acc
    }, {})
  }

  private removePrefixedProps(
    prefix: string,
    object: Dictionary<string>
  ): Dictionary<string> {
    if (!prefix) return object

    return Object.entries(object).reduce((acc, [key, value]) => {
      if (
        !key.startsWith(prefix) ||
        this.isPageSizeKey(this.stripPrefix(key, prefix))
      ) {
        return { ...acc, [key]: value }
      }
      return acc
    }, {})
  }
}

// Export a singleton instance
const routerHandler = new RouterHandler()
export default routerHandler
